import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/App';
import { SHARE_DOMAIN } from '@/lib/shareDomain';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Cake, Heart, MessageCircle, Calendar, Loader2, Eye, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Celebrations — upcoming birthdays & anniversaries across customers, their
 * family members, and tour group_members. One-tap WhatsApp send with a
 * branded card image.
 *
 * Modes:
 *  - compact: small dashboard widget showing counts + top 5 events
 *  - full:    a complete page with filters + every event row
 */

const fetchEvents = async (days) => {
  const t = localStorage.getItem('session_token');
  const res = await fetch(`${API}/pollen/celebrations?days=${days}`, {
    credentials: 'include',
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  if (!res.ok) throw new Error('Failed to load');
  return res.json();
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
};

// Display-only phone formatter: ensures a leading '+' and groups digits a
// little so the operator can sanity-check the number at a glance.
const formatPhone = (p) => {
  if (!p) return '';
  const digits = String(p).replace(/\D+/g, '');
  if (!digits) return p;
  // India default: 10 digits or '0'+10
  let canonical = digits;
  if (canonical.length === 11 && canonical.startsWith('0') && /[6789]/.test(canonical[1])) {
    canonical = '91' + canonical.slice(1);
  } else if (canonical.length === 10 && /[6789]/.test(canonical[0])) {
    canonical = '91' + canonical;
  } else if (canonical.startsWith('00')) {
    canonical = canonical.slice(2);
  }
  // Group as "+CC NNNNN NNNNN" for India-style display
  if (canonical.length === 12 && canonical.startsWith('91')) {
    return `+91 ${canonical.slice(2, 7)} ${canonical.slice(7)}`;
  }
  return '+' + canonical;
};

const daysLabel = (n) => {
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n <= 6) return `In ${n} days`;
  return `In ${n} days`;
};

const buildShareUrl = (e, asHtml = false) => {
  // Card lives on pollendots.com (same host as the customer journey share
  // page). For WhatsApp messages we send the HTML wrapper so WhatsApp's
  // crawler unfurls a proper link preview (it won't preview direct .png
  // URLs). For the in-app lightbox we keep the direct .png.
  const ORIGIN = SHARE_DOMAIN;
  const params = new URLSearchParams({
    name: e.name || '',
    kind: e.kind || 'birthday',
  });
  // Team members get a brand-voiced wish (Pollen Dots thanking them) —
  // forward `source=team` so the card image + WhatsApp teaser switch
  // to the team copy.
  if ((e.source || '').toLowerCase() === 'team') params.set('source', 'team');
  // Minute-level cache buster so WhatsApp re-scrapes the og:image on
  // every fresh send instead of serving stale cached previews.
  params.set('v', String(Math.floor(Date.now() / 60000)));
  if (asHtml) {
    return `${ORIGIN}/api/public/wish?${params.toString()}`;
  }
  return `${ORIGIN}/api/public/celebration-card.png?${params.toString()}`;
};

const sendWish = async (e, overridePhone = null) => {
  const t = localStorage.getItem('session_token');
  const targetPhone = (overridePhone || e.contact_phone || '').trim();
  if (!targetPhone) {
    toast.error(overridePhone ? 'Set a valid test number first' : 'No phone number on file for this contact');
    return;
  }
  try {
    // Step 1: mint a short, branded wish URL (DB-backed, 7-day silent
    // expiry computed from the next celebration date). Falls back to the
    // long stateless /api/public/wish URL if minting fails for any reason.
    let shortUrl = '';
    try {
      const r = await fetch(`${API}/pollen/celebrations/wish-link`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({
          name: e.name || '',
          kind: e.kind || 'birthday',
          next_date: e.next_date || '',
          source: e.source || '',
        }),
      });
      if (r.ok) {
        const j = await r.json();
        shortUrl = j.public_url || '';
      }
    } catch (_) { /* fall through to long URL */ }
    const cardUrl = shortUrl || buildShareUrl(e, true);

    // Step 2: build wa.me link with the wish + the short URL
    const qs = new URLSearchParams({
      phone: targetPhone,
      kind: e.kind || 'birthday',
      name: e.name || '',
      card_url: cardUrl,
    });
    if ((e.source || '').toLowerCase() === 'team') qs.set('source', 'team');
    const res = await fetch(`${API}/pollen/celebrations/whatsapp-link?${qs.toString()}`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to build link');
    if (!data.whatsapp_url) { toast.error('Could not build WhatsApp link'); return; }
    window.open(data.whatsapp_url, '_blank', 'noopener');
  } catch (err) {
    toast.error(err.message);
  }
};

const KindBadge = ({ kind, days_until }) => (
  <span className={`text-[10px] uppercase tracking-wide font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
    days_until === 0
      ? (kind === 'anniversary' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white')
      : (kind === 'anniversary' ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-amber-50 text-amber-800 border border-amber-200')
  }`}>
    {kind === 'anniversary' ? <Heart className="w-2.5 h-2.5" /> : <Cake className="w-2.5 h-2.5" />}
    {kind === 'anniversary' ? 'Anniv' : 'Birthday'}
  </span>
);

export const CelebrationsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    fetchEvents(30).then(d => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="celebrations-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;
  const top = (data.events || []).slice(0, 5);

  return (
    <Card data-testid="celebrations-widget" className="border-amber-200/60">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <CardTitle className="text-base">Celebrations</CardTitle>
        </div>
        <a
          href="/pollen-dots/celebrations"
          className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-0.5"
          data-testid="celebrations-widget-all"
        >See all <ExternalLink className="w-3 h-3" /></a>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <Pill label="Today" value={data.today} accent={data.today > 0} />
          <Pill label="This week" value={data.this_week} />
          <Pill label="This month" value={data.this_month} />
        </div>
        {top.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing in the next 30 days. Add DOBs/anniversaries on the customer profiles.</p>
        ) : (
          <ul className="divide-y" data-testid="celebrations-widget-list">
            {top.map(e => <CelebrationRow key={e.id} e={e} compact />)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const Pill = ({ label, value, accent }) => (
  <div className={`text-center rounded-lg border px-2 py-1.5 ${accent ? 'bg-amber-100 border-amber-300' : 'bg-amber-50/40 border-amber-100'}`}>
    <div className="text-lg font-bold tabular-nums leading-none">{value}</div>
    <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const CelebrationRow = ({ e, compact, testPhone = '' }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const cardUrl = useMemo(() => buildShareUrl(e), [e]);
  const hasTest = !!(testPhone && testPhone.replace(/\D+/g, '').length >= 7);
  return (
    <li className={`py-2 ${compact ? '' : 'sm:py-3'} flex items-center gap-3`} data-testid={`celeb-row-${e.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{e.name}</span>
          <KindBadge kind={e.kind} days_until={e.days_until} />
          {e.relation && e.relation !== 'Self' && (
            <span className="text-[10px] text-muted-foreground">· {e.relation}</span>
          )}
          {e.kind === 'birthday' && Number(e.age) > 0 && (
            <></>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
          <Calendar className="w-3 h-3 inline mr-1" />{fmtDate(e.next_date)} · {daysLabel(e.days_until)}
          {e.customer_name && e.customer_name !== e.name && (
            <> · via <span className="text-foreground/80">{e.customer_name}</span></>
          )}
        </div>
        <div className="text-[11px] mt-0.5 truncate">
          {e.contact_phone ? (
            <span className={e.sent_to_customer ? 'text-amber-700' : 'text-emerald-700'}>
              <MessageCircle className="w-3 h-3 inline mr-1" />
              {e.sent_to_customer
                ? <>Will WhatsApp <strong>{e.customer_name || 'customer'}</strong> at {formatPhone(e.contact_phone)} (no direct number for {e.name})</>
                : <>To <strong>{e.contact_name || e.name}</strong> at {formatPhone(e.contact_phone)}</>
              }
            </span>
          ) : (
            <span className="text-rose-600">No phone number on file</span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs px-1.5 text-amber-700"
        onClick={() => setPreviewOpen(true)}
        data-testid={`celeb-preview-${e.id}`}
        title="Preview card"
      >
        <Eye className="w-3.5 h-3.5" />
      </Button>
      {hasTest && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs px-2 border-slate-300 text-slate-700 hover:bg-slate-100"
          onClick={() => sendWish(e, testPhone)}
          data-testid={`celeb-test-${e.id}`}
          title={`Send to test number ${formatPhone(testPhone)} (you, not the guest)`}
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1" />Test
        </Button>
      )}
      <Button
        size="sm"
        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
        onClick={() => sendWish(e)}
        disabled={!e.contact_phone}
        data-testid={`celeb-send-${e.id}`}
        title={e.contact_phone ? 'Open WhatsApp with the wish ready' : 'No phone number on the customer record'}
      >
        <MessageCircle className="w-3.5 h-3.5 mr-1" />Wish
      </Button>
      {previewOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div className="bg-white rounded-lg p-2 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <img src={cardUrl} alt={`Card for ${e.name}`} className="w-full rounded-md" />
            <div className="flex justify-between items-center mt-2 px-1">
              <a href={cardUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-700 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />Open card in new tab
              </a>
              <Button size="sm" variant="ghost" onClick={() => setPreviewOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export const CelebrationsFull = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [testPhone, setTestPhone] = useState(() => localStorage.getItem('celeb_test_phone') || '');
  const saveTestPhone = (v) => {
    setTestPhone(v);
    if (v.trim()) localStorage.setItem('celeb_test_phone', v.trim());
    else localStorage.removeItem('celeb_test_phone');
  };

  const reload = () => {
    setLoading(true);
    fetchEvents(days).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(reload, [days]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!data?.events) return [];
    return data.events.filter(e => {
      if (kind !== 'all' && e.kind !== kind) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return [e.name, e.customer_name, e.relation].some(v => (v || '').toLowerCase().includes(s));
    });
  }, [data, q, kind]);

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-20" data-testid="celebrations-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-600" />Celebrations
        </h1>
        <p className="text-sm text-muted-foreground">Upcoming birthdays &amp; anniversaries across your customers, their families and co-travellers.</p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Pill label="Today" value={data?.today ?? 0} accent={(data?.today ?? 0) > 0} />
            <Pill label="This week" value={data?.this_week ?? 0} />
            <Pill label={`Next ${days} days`} value={data?.this_month ?? 0} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex bg-muted rounded-md p-0.5 text-xs">
              {[7, 30, 90, 365].map(n => (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  className={`px-2.5 py-1 rounded ${days === n ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  data-testid={`celeb-window-${n}`}
                >{n === 365 ? 'Year' : `${n}d`}</button>
              ))}
            </div>
            <div className="flex bg-muted rounded-md p-0.5 text-xs">
              {[['all', 'All'], ['birthday', 'Birthdays'], ['anniversary', 'Anniversaries']].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  className={`px-2.5 py-1 rounded ${kind === k ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  data-testid={`celeb-kind-${k}`}
                >{l}</button>
              ))}
            </div>
            <Input
              placeholder="Search name or customer…"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="h-8 text-sm flex-1 min-w-[140px]"
              data-testid="celeb-search"
            />
          </div>
          {/* Test-send number: when set, every row shows a "Test" button that
             fires the wish to THIS phone instead of the guest's. Persisted in
             localStorage so the operator only sets it once per device. */}
          <div className="flex items-center gap-2 pt-1 border-t border-amber-100/60">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">
              Test number
            </label>
            <Input
              type="tel"
              placeholder="+91 98xxxxxxxx (only you receive)"
              value={testPhone}
              onChange={e => saveTestPhone(e.target.value)}
              className="h-8 text-sm flex-1 max-w-xs"
              data-testid="celeb-test-phone"
            />
            {testPhone && (
              <button
                onClick={() => saveTestPhone('')}
                className="text-[11px] text-muted-foreground hover:text-rose-600"
                data-testid="celeb-test-phone-clear"
              >Clear</button>
            )}
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              {testPhone ? 'Each row shows a Test button →' : 'Set a number to enable Test sends'}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No matches in this window. Add DOBs / anniversaries to family members on customer profiles to populate this list.
            </div>
          ) : (
            <ul className="divide-y px-3" data-testid="celebrations-list">
              {filtered.map(e => <CelebrationRow key={e.id} e={e} testPhone={testPhone} />)}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
